import { Input, InputNumber, Select } from "antd";
import classNames from "classnames";
import { deburr, get, isNil, omit, uniqBy } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import CanvasIcon from "./CanvasIcon";
import { fieldOperandOf } from "./catalog";
import { ICON_PATHS } from "./step-look";
import { WORKSHOP_ICONS } from "./workshop-view";

const TABS = { VALUE: "value", FIELD: "field" };

const FIELD_PREFIX = "field:";
const VALUE_PREFIX = "value:";
const TYPED_KEY = "typed";

const normalized = (text) => deburr(String(text || "")).toLowerCase();

const matches = (search) => (option) =>
  normalized(get(option, "label")).includes(normalized(search).trim());

const hasValue = (value) => !isNil(value) && value !== "";

const tabOf = (condition) =>
  fieldOperandOf(condition) === null ? TABS.VALUE : TABS.FIELD;

const selectionOf = ({ condition, fields, valueOptions }) => {
  const accessor = fieldOperandOf(condition);
  if (accessor !== null) {
    if (!accessor) return undefined;
    const field = fields.find((option) => option.value === accessor);
    return {
      value: `${FIELD_PREFIX}${accessor}`,
      label: (
        <span className="condition-value-picker__field">
          <CanvasIcon path={WORKSHOP_ICONS.input} size={14} />
          <span className="condition-value-picker__field-label">
            {get(field, "label") || accessor}
          </span>
        </span>
      ),
    };
  }
  const value = get(condition, "rightOperand.value");
  if (!hasValue(value)) return undefined;
  const index = (valueOptions || []).findIndex(
    (option) => option.value === value,
  );
  if (index === -1) return { value: TYPED_KEY, label: String(value) };
  return {
    value: `${VALUE_PREFIX}${index}`,
    label: valueOptions[index].label,
  };
};

const ConditionValuePicker = ({
  condition,
  fields,
  valueOptions,
  valueType,
  labels,
  className,
  onChange,
  ...rest
}) => {
  const selectRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(() => tabOf(condition));
  const [search, setSearch] = useState("");
  const typed = tab === TABS.VALUE && !get(valueOptions, "length");

  const switchTab = (next) => {
    if (next === tab) return;
    if (typed) selectRef.current?.focus();
    setTab(next);
    setSearch("");
  };

  useEffect(() => {
    if (!open || typed) return undefined;
    const timeoutId = setTimeout(() => searchRef.current?.focus());
    return () => clearTimeout(timeoutId);
  }, [open, tab, typed]);

  const tabOptions =
    tab === TABS.FIELD
      ? uniqBy(fields, "value").map((option) => ({
          value: `${FIELD_PREFIX}${option.value}`,
          label: option.label,
        }))
      : (valueOptions || []).map((option, index) => ({
          value: `${VALUE_PREFIX}${index}`,
          label: option.label,
        }));
  const options = search ? tabOptions.filter(matches(search)) : tabOptions;

  const writeValue = (value) =>
    onChange({ ...omit(condition.rightOperand || {}, ["accessor"]), value });

  const onSelect = (selected) => {
    const key = get(selected, "value", "");
    if (key.startsWith(FIELD_PREFIX))
      return onChange({ accessor: key.slice(FIELD_PREFIX.length) });
    if (key.startsWith(VALUE_PREFIX)) {
      const option = valueOptions[Number(key.slice(VALUE_PREFIX.length))];
      return writeValue(get(option, "value"));
    }
    return null;
  };

  const currentValue =
    fieldOperandOf(condition) === null
      ? get(condition, "rightOperand.value")
      : null;

  const typedKeys = (event) => {
    event.stopPropagation();
    if (!["Enter", "Escape"].includes(event.key)) return;
    setOpen(false);
    selectRef.current?.focus();
  };

  const typedInput =
    valueType === "number" ? (
      <InputNumber
        autoFocus
        className="condition-value-picker__input"
        aria-label={labels.TAB_VALUE}
        placeholder={labels.TAB_VALUE}
        value={isNil(currentValue) || currentValue === "" ? null : currentValue}
        onKeyDown={typedKeys}
        onChange={(next) => writeValue(isNil(next) ? null : next)}
      />
    ) : (
      <Input
        autoFocus
        className="condition-value-picker__input"
        aria-label={labels.TAB_VALUE}
        placeholder={labels.TAB_VALUE}
        value={isNil(currentValue) ? "" : String(currentValue)}
        onKeyDown={typedKeys}
        onChange={(event) => writeValue(event.target.value)}
      />
    );

  const searchKeys = (event) => {
    if (["ArrowUp", "ArrowDown"].includes(event.key)) event.preventDefault();
    if (["Enter", "Escape"].includes(event.key)) selectRef.current?.focus();
  };

  const tabs = [
    { value: TABS.VALUE, label: labels.TAB_VALUE },
    { value: TABS.FIELD, label: labels.TAB_FIELD },
  ];

  return (
    <Select
      {...rest}
      ref={selectRef}
      labelInValue
      className={classNames("condition-value-picker", className)}
      popupClassName="condition-value-picker__popup"
      popupMatchSelectWidth={false}
      open={open}
      showSearch={false}
      filterOption={false}
      options={options}
      notFoundContent={
        <div className="condition-value-picker__empty">{labels.NO_RESULT}</div>
      }
      value={selectionOf({ condition, fields, valueOptions })}
      onDropdownVisibleChange={(next) => {
        if (next) setTab(tabOf(condition));
        setSearch("");
        setOpen(next);
      }}
      onKeyDown={(event) => {
        if (!open || search) return;
        if (event.key === "ArrowLeft") switchTab(TABS.VALUE);
        if (event.key === "ArrowRight") switchTab(TABS.FIELD);
      }}
      onChange={onSelect}
      dropdownRender={(menu) => (
        <div
          className="condition-value-picker__panel"
          onMouseDown={(event) => {
            if (event.target !== searchRef.current) event.preventDefault();
          }}
        >
          <div className="condition-value-picker__tabs" role="tablist">
            {tabs.map((option) => (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={tab === option.value}
                className="condition-value-picker__tab"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => switchTab(option.value)}
              >
                {option.value === TABS.FIELD ? (
                  <CanvasIcon path={WORKSHOP_ICONS.input} size={13} />
                ) : null}
                <span className="condition-value-picker__tab-label">
                  {option.label}
                </span>
              </button>
            ))}
          </div>
          {typed ? (
            <div className="condition-value-picker__typed">{typedInput}</div>
          ) : (
            <>
              <Input
                ref={searchRef}
                allowClear
                size="small"
                className="condition-value-picker__search"
                aria-label={labels.SEARCH}
                placeholder={labels.SEARCH}
                prefix={
                  <CanvasIcon
                    path={ICON_PATHS.search}
                    size={14}
                    strokeWidth={1.9}
                  />
                }
                value={search}
                onKeyDown={searchKeys}
                onChange={(event) => setSearch(event.target.value)}
              />
              {menu}
            </>
          )}
        </div>
      )}
    />
  );
};

ConditionValuePicker.propTypes = {
  condition: PropTypes.object.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.node }),
  ).isRequired,
  valueOptions: PropTypes.array,
  valueType: PropTypes.string,
  labels: PropTypes.shape({
    TAB_VALUE: PropTypes.string,
    TAB_FIELD: PropTypes.string,
    SEARCH: PropTypes.string,
    NO_RESULT: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

ConditionValuePicker.defaultProps = {
  valueOptions: null,
  valueType: "text",
  className: undefined,
};

export default ConditionValuePicker;
